import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type React from 'react';
import type { ValidationStatus } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LineData = {
  topId: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  status: 'idle' | 'correct' | 'incorrect';
};

interface UsePairLinesOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  pairs: Record<string, string>;
  lineDirections: Record<string, 'top-to-bottom' | 'bottom-to-top'>;
  validationStatus: ValidationStatus | null | undefined;
  pairStatusByTopId: Record<string, 'correct' | 'incorrect'>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages SVG connector-line geometry for the PairMatcher widget.
 *
 * Returns a stable `dotRefs` map so callers can register DOM dot elements,
 * and a `lines` array that is recalculated after every layout paint in which
 * pairs or validation state changes, and again on container resize.
 */
export function usePairLines({
  containerRef,
  pairs,
  lineDirections,
  validationStatus,
  pairStatusByTopId,
}: UsePairLinesOptions): {
  dotRefs: React.MutableRefObject<Map<string, HTMLSpanElement>>;
  lines: LineData[];
} {
  const dotRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
  const [lines, setLines] = useState<LineData[]>([]);

  const recalcLines = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const cr = container.getBoundingClientRect();
    const newLines: LineData[] = [];

    for (const [topId, bottomId] of Object.entries(pairs)) {
      const topDot    = dotRefs.current.get(`top:${topId}`);
      const bottomDot = dotRefs.current.get(`bottom:${bottomId}`);
      if (!topDot || !bottomDot) continue;

      const tr = topDot.getBoundingClientRect();
      const br = bottomDot.getBoundingClientRect();

      const status: LineData['status'] = validationStatus
        ? (pairStatusByTopId[topId] ?? (validationStatus === 'correct' ? 'correct' : 'incorrect'))
        : 'idle';

      const direction = lineDirections[topId] ?? 'top-to-bottom';
      const startRect = direction === 'top-to-bottom' ? tr : br;
      const endRect   = direction === 'top-to-bottom' ? br : tr;

      newLines.push({
        topId,
        x1: startRect.left - cr.left + startRect.width  / 2,
        y1: startRect.top  - cr.top  + startRect.height / 2,
        x2: endRect.left   - cr.left + endRect.width    / 2,
        y2: endRect.top    - cr.top  + endRect.height   / 2,
        status,
      });
    }

    setLines(newLines);
  }, [pairs, lineDirections, validationStatus, pairStatusByTopId]);

  // Recalculate immediately after every layout paint that changes pairs/validation.
  useLayoutEffect(() => {
    recalcLines();
  }, [recalcLines]);

  // Recalculate on container resize (e.g. window resize, orientation change).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(recalcLines);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, recalcLines]);

  return { dotRefs, lines };
}
